/** 商品编辑工具（对齐 back-web GoodsEditDrawer） */

export const DEFAULT_SPECS_PRICE_COLUMNS = ['350', '500', '650', '800', '1000'];

export function colKey(label) {
  const map = { '350': 'one', '500': 'two', '650': 'three', '800': 'four', '1000': 'five' };
  return map[label] || 'one';
}

export function emptySpecsValueRow() {
  return { title: '', price: '' };
}

export function emptySpecsPriceRow() {
  return { title: '', one: '', two: '', three: '', four: '', five: '' };
}

export function stripCurrencySymbol(value) {
  return String(value ?? '').trim().replace(/^[¥￥]\s*/, '');
}

export function formatIntervalPrice(value) {
  const raw = stripCurrencySymbol(value);
  if (!raw) return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toFixed(2);
}

/** 失焦展示：输入框内带人民币符号 */
export function formatCurrencyDisplay(value) {
  const formatted = formatIntervalPrice(value);
  if (!formatted) return '';
  return `¥${formatted}`;
}

export function stripMmSuffix(value) {
  return String(value ?? '').trim().replace(/\s*mm\s*$/i, '');
}

export function formatMmValue(value) {
  const raw = stripMmSuffix(value);
  if (!raw) return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return String(n);
}

/** 失焦展示：输入框内带 mm 单位 */
export function formatMmDisplay(value) {
  const formatted = formatMmValue(value);
  if (!formatted) return '';
  return `${formatted}mm`;
}

export function normalizeIsSku(_v) {
  // 已取消单规格：任意历史值均按多规格处理
  return 1;
}

export function normalizeParameterIds(raw) {
  if (Array.isArray(raw)) {
    return raw.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  }
  return String(raw ?? '')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((id) => Number.isFinite(id) && id > 0);
}

export function cloneSpecsValueList(list) {
  return list.map((r) => ({ ...r }));
}

export function cloneSpecsPriceList(list) {
  return list.map((r) => ({ ...r }));
}

export function cloneSkuList(list) {
  return list.map((s) => ({
    ...s,
    children: (s.children || []).map((c) => ({ ...c, children: [] })),
  }));
}

export function cloneSkuPrice(list) {
  return list.map((r) => ({
    ...r,
    goods_sku_text: [...(r.goods_sku_text || [])],
    goods_sku_temp_ids: [...(r.goods_sku_temp_ids || [])],
  }));
}

export function normalizeSpecsValueList(rows) {
  rows.forEach((row) => {
    if (row.price) row.price = formatIntervalPrice(row.price);
  });
}

export function normalizeSpecsPriceList(rows) {
  rows.forEach((row) => {
    ['one', 'two', 'three', 'four', 'five'].forEach((key) => {
      if (row[key]) row[key] = formatIntervalPrice(row[key]);
    });
  });
}

function parseMmNumber(val, fallback = 0) {
  if (val == null || val === '') return fallback
  const n = Number(stripMmSuffix(val))
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

export function normalizeSkuPriceList(rows) {
  return (rows || []).map((row, index) => ({
    ...row,
    weigh: index + 1,
    price: row.price != null && row.price !== '' ? Number(formatIntervalPrice(row.price)) : 0,
    weight: row.weight != null && row.weight !== '' ? Number(stripMmSuffix(row.weight)) : 0,
    stock_length: parseMmNumber(row.stock_length, 3000),
    // 未配置时默认关闭；仅显式 true/1 视为开启
    switch_enable: row.switch_enable === true || row.switch_enable === 1 || row.switch_enable === '1',
  }));
}

export const LIGHT_STRIP_ROOT_CATEGORY_ID = 2;

export function resolveCategoryRootId(categories, categoryId) {
  const cid = Number(categoryId);
  if (!cid) return 0;
  const map = new Map((categories || []).map((c) => [Number(c.id), c]));
  let cur = map.get(cid);
  let guard = 0;
  while (cur && Number(cur.pid) && guard++ < 20) {
    cur = map.get(Number(cur.pid));
  }
  return cur ? Number(cur.id) : cid;
}

export function isLightStripCategory(categories, categoryId) {
  return resolveCategoryRootId(categories, categoryId) === LIGHT_STRIP_ROOT_CATEGORY_ID;
}

export function isLightStripRootId(rootId) {
  return Number(rootId) === LIGHT_STRIP_ROOT_CATEGORY_ID;
}

export function normalizeAddSizeSwitch(data, categories) {
  const defaultOn = isLightStripCategory(categories, data.category_id);
  return Number(data.add_size ?? (defaultOn ? 1 : 0)) !== 0;
}

export function rootCategories(categories) {
  return (categories || []).filter((c) => !c.pid || Number(c.pid) === 0);
}

export function childCategories(categories, pid) {
  return (categories || []).filter((c) => Number(c.pid) === Number(pid));
}

/** 某产品分类下的全部商品分类（含多级），按树序展开 */
export function flattenDescendantCategories(categories, rootId, depth = 1) {
  const kids = childCategories(categories, rootId);
  const out = [];
  kids.forEach((c) => {
    const prefix = depth > 1 ? `${'　'.repeat(depth - 1)}` : '';
    out.push({ ...c, name: `${prefix}${c.name}`, rawName: c.name, _depth: depth });
    out.push(...flattenDescendantCategories(categories, c.id, depth + 1));
  });
  return out;
}

export function resolveCategoryTab(categories, categoryId) {
  const root = resolveCategoryRootId(categories, categoryId);
  if (root) return String(root);
  const roots = rootCategories(categories);
  return roots.length ? String(roots[0].id) : '';
}

export function defaultCategoryId(categories) {
  const roots = rootCategories(categories);
  if (!roots.length) return '';
  const children = childCategories(categories, roots[0].id);
  return children.length ? children[0].id : '';
}

export function defaultGoodsForm(categories) {
  return {
    id: 0,
    name: '',
    model: '',
    category_id: defaultCategoryId(categories),
    unit: '',
    price: 0,
    valuation: 1,
    status: true,
    sort: 0,
    image: '',
    images_list: [],
    content: '',
    summary: '',
    switch: true,
    tag_switch: true,
    add_size: isLightStripCategory(categories, defaultCategoryId(categories)),
    saw_kerf: 3.5,
    end_trim: 20,
    is_sku: 1,
    parameter_id_list: [],
    specs_value_list: [],
    specs_price_list: [],
  };
}

export function buildSubmitPayload(form, skuList, skuPrice) {
  const row = {
    name: form.name,
    model: form.model,
    category_id: form.category_id,
    unit: form.unit,
    price: form.price,
    valuation: form.valuation,
    status: form.status,
    sort: form.sort,
    image: form.image,
    images: (form.images_list || []).filter(Boolean).join(','),
    content: form.content,
    summary: form.summary,
    switch: form.switch,
    tag_switch: form.tag_switch,
    add_size: form.add_size ? 1 : 0,
    saw_kerf: parseMmNumber(form.saw_kerf, 3.5),
    end_trim: parseMmNumber(form.end_trim, 20),
    // 固定多规格；用 1/0，避免 qs.stringify 把 true 编成 "true"
    is_sku: 1,
    parameter_ids: (form.parameter_id_list || []).join(','),
    specs_value: JSON.stringify(form.specs_value_list || []),
    specs_price: JSON.stringify(form.specs_price_list || []),
    type: '2',
  };
  if (form.id) row.id = form.id;
  row.sku = {
    listData: JSON.stringify(skuList || []),
    priceData: JSON.stringify(normalizeSkuPriceList(skuPrice)),
  };
  return row;
}

export function fillFormFromDetail(data, categories = []) {
  const imagesList = Array.isArray(data.images_list)
    ? data.images_list.map(String)
    : String(data.images || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  return {
    id: data.id || 0,
    name: data.name || '',
    model: data.model || '',
    category_id: data.category_id || '',
    unit: data.unit || '',
    price: Number(data.price ?? 0),
    valuation: Number(String(data.valuation || '1').replace(/^v/, '')) || 1,
    status: data.status !== false && data.status !== 0 && data.status !== '0',
    sort: Number(data.sort ?? 0),
    image: data.image || '',
    images_list: imagesList,
    content: data.content || '',
    summary: data.summary || '',
    switch: data.switch !== false && data.switch !== 0 && data.switch !== '0',
    tag_switch: data.tag_switch !== false && data.tag_switch !== 0 && data.tag_switch !== '0',
    add_size: normalizeAddSizeSwitch(data, categories),
    saw_kerf: parseMmNumber(data.saw_kerf, 3.5),
    end_trim: parseMmNumber(data.end_trim, 20),
    is_sku: 1,
    parameter_id_list: normalizeParameterIds(data.parameter_id_list ?? data.parameter_ids),
    specs_value_list: [],
    specs_price_list: Array.isArray(data.specs_price_list) ? data.specs_price_list : [],
  };
}

export function initSkuFromDetail(list, prices, countIdRef) {
  let countId = countIdRef.value;
  const tempIdArr = {};
  const nextList = (list || []).map((main) => {
    const tempId = countId++;
    const children = ((main.children || [])).map((child) => {
      const childTempId = countId++;
      if (child.id) tempIdArr[Number(child.id)] = childTempId;
      return {
        id: Number(child.id || 0),
        temp_id: childTempId,
        name: String(child.name || ''),
        pid: Number(main.id || 0),
        children: [],
      };
    });
    return {
      id: Number(main.id || 0),
      temp_id: tempId,
      name: String(main.name || ''),
      pid: 0,
      children,
    };
  });
  const skuPrice = (prices || []).map((item, index) => {
    const goodsSkuText = Array.isArray(item.goods_sku_text)
      ? item.goods_sku_text
      : String(item.goods_sku_text || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
    const goodsSkuTempIds = String(item.goods_sku_ids || '')
      .split(',')
      .map((id) => tempIdArr[Number(id.trim())])
      .filter((id) => id != null);
    return {
      id: Number(item.id || 0),
      temp_id: index + 1,
      goods_sku_ids: String(item.goods_sku_ids || ''),
      goods_sku_temp_ids: goodsSkuTempIds,
      goods_sku_text: goodsSkuText,
      image: String(item.image || ''),
      price: item.price != null && item.price !== '' ? Number(item.price) : 0,
      weight: item.weight != null && item.weight !== '' ? Number(stripMmSuffix(item.weight)) : 0,
      stock_length: parseMmNumber(item.stock_length, 3000),
      sn: String(item.sn || ''),
      status: item.status === 'down' ? 'down' : 'up',
      // 未配置时默认关闭；仅显式 true/1 视为开启
      switch_enable: item.switch_enable === true || item.switch_enable === 1 || item.switch_enable === '1',
    };
  });
  countIdRef.value = countId;
  return { skuList: nextList, skuPrice };
}
